import { useApolloClient } from '@apollo/client';
import { Button, Card, Col, Form, message, Row, Space } from 'antd';
import { Formik, useFormikContext } from 'formik';
import { omit } from 'lodash/fp';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { type MyInfoModuleSpecsFragment } from '../../../../api/fragments/MyInfoModuleSpecs';
import { type MyInfoSettingSpecFragment } from '../../../../api/fragments/MyInfoSettingSpec';
import {
    UpdateMyInfoSettingDocument,
    type UpdateMyInfoSettingMutation,
    type UpdateMyInfoSettingMutationVariables,
} from '../../../../api/mutations/updateMyInfoSetting';
import { type MyInfoSettingInput, MyInfoSettingVersion } from '../../../../api/types';
import InputField from '../../../../components/fields/InputField';
import PasswordField from '../../../../components/fields/PasswordField';
import SelectField from '../../../../components/fields/SelectField';
import SingleSecretUploadField from '../../../../components/fields/SingleSecretUploadField';
import SwitchField from '../../../../components/fields/SwitchField';
import ConsolePageWithHeader from '../../../../layouts/ConsoleLayout/ConsolePageWithHeader';
import useHandleError from '../../../../utilities/useHandleError';
import useSystemSwitchData from '../../../../utilities/useSystemSwitchData';
import useValidator from '../../../../utilities/useValidator';
import { myInfoFormValidator } from './AddMyInfoSettingsForm';
import useMyinfoVersionOptions from './useMyinfoVersionOptions';

export type FormValues = MyInfoSettingInput;

export type UpdateMyInfoSettingInnerPageProps = {
    module: MyInfoModuleSpecsFragment;
    myInfoSettings: MyInfoSettingSpecFragment;
};

const colSpan = { lg: 8, md: 12, xs: 24 };

const FormFields = () => {
    const { t } = useTranslation(['myInfoModuleDetails']);
    const { yesNoSwitch } = useSystemSwitchData();
    const versionOptions = useMyinfoVersionOptions();
    const {
        values: { version },
    } = useFormikContext<FormValues>();

    return (
        <Row gutter={14}>
            <Col {...colSpan}>
                <InputField
                    {...t('myInfoModuleDetails:myInfoSettingsDrawer.fields.displayName', {
                        returnObjects: true,
                    })}
                    name="displayName"
                    required
                />
            </Col>
            <Col {...colSpan}>
                <SelectField
                    {...t('myInfoModuleDetails:myInfoSettingsDrawer.fields.version', {
                        returnObjects: true,
                    })}
                    name="version"
                    options={versionOptions}
                    showSearch
                />
            </Col>
            <Col {...colSpan}>
                <SwitchField
                    {...t('myInfoModuleDetails:myInfoSettingsDrawer.fields.isTestEnvironment', {
                        returnObjects: true,
                    })}
                    name="isTestEnvironment"
                    {...yesNoSwitch}
                />
            </Col>
            <Col {...colSpan}>
                <InputField
                    {...t('myInfoModuleDetails:myInfoSettingsDrawer.fields.clientId', {
                        returnObjects: true,
                    })}
                    name="clientId"
                />
            </Col>
            <Col {...colSpan}>
                <InputField
                    {...t('myInfoModuleDetails:myInfoSettingsDrawer.fields.baseUrl', {
                        returnObjects: true,
                    })}
                    name="baseUrl"
                />
            </Col>
            {version === MyInfoSettingVersion.MyInfoV3 && (
                <>
                    <Col {...colSpan}>
                        <InputField
                            {...t('myInfoModuleDetails:myInfoSettingsDrawer.fields.requestPurpose', {
                                returnObjects: true,
                            })}
                            name="requestPurpose"
                        />
                    </Col>
                    <Col {...colSpan}>
                        <PasswordField
                            {...t('myInfoModuleDetails:myInfoSettingsDrawer.fields.clientSecret', {
                                returnObjects: true,
                            })}
                            name="clientSecret"
                            placeholder="*******"
                        />
                    </Col>
                    <Col {...colSpan}>
                        <SingleSecretUploadField
                            {...t('myInfoModuleDetails:myInfoSettingsDrawer.fields.privateKey', {
                                returnObjects: true,
                            })}
                            name="privateKey"
                        />
                    </Col>
                    <Col {...colSpan}>
                        <SingleSecretUploadField
                            {...t('myInfoModuleDetails:myInfoSettingsDrawer.fields.publicCertificate', {
                                returnObjects: true,
                            })}
                            name="publicCertificate"
                        />
                    </Col>
                    <Col {...colSpan}>
                        <SwitchField
                            {...t('myInfoModuleDetails:myInfoSettingsDrawer.fields.pkiSignature', {
                                returnObjects: true,
                            })}
                            name="pkiSignature"
                            {...yesNoSwitch}
                        />
                    </Col>
                </>
            )}
        </Row>
    );
};

const UpdateMyInfoSettingInnerPage = ({ module, myInfoSettings }: UpdateMyInfoSettingInnerPageProps) => {
    const myInfoModuleId = module.id;
    const myInfoSettingsId = myInfoSettings.id;

    const { t } = useTranslation(['myInfoModuleDetails']);
    const apolloClient = useApolloClient();
    const navigate = useNavigate();

    const initialValues = useMemo(
        (): FormValues => ({
            ...omit(['id'], myInfoSettings),
        }),
        [myInfoSettings]
    );

    const onSubmit = useHandleError(
        async (values: FormValues) => {
            message.loading({
                content: t('myInfoModuleDetails:myInfoSettingsDrawer.messages.updateSubmitting'),
                key: 'primary',
                duration: 0,
            });

            await apolloClient
                .mutate<UpdateMyInfoSettingMutation, UpdateMyInfoSettingMutationVariables>({
                    mutation: UpdateMyInfoSettingDocument,
                    variables: { myInfoModuleId, id: myInfoSettingsId, secrets: values },
                })
                .finally(() => {
                    message.destroy('primary');
                });

            message.success({
                content: t('myInfoModuleDetails:myInfoSettingsDrawer.messages.updateSuccess'),
            });

            navigate(`/admin/system/modules/${myInfoModuleId}`);
        },
        [t, apolloClient, myInfoModuleId, myInfoSettingsId, navigate]
    );

    const validate = useValidator(myInfoFormValidator);

    return (
        <ConsolePageWithHeader
            footer={[
                <Button key="submit" form="myInfoSettingsForm" htmlType="submit" type="primary">
                    {t('myInfoModuleDetails:myInfoSettingsDrawer.updateButton')}
                </Button>,
            ]}
            onBack={() => navigate(`/admin/system/modules/${myInfoModuleId}`)}
            title={t('myInfoModuleDetails:myInfoSettingsDrawer.title.update')}
        >
            <Formik initialValues={initialValues} onSubmit={onSubmit} validate={validate}>
                {({ handleSubmit }) => (
                    <Form
                        id="myInfoSettingsForm"
                        layout="vertical"
                        name="myInfoSettingsForm"
                        onSubmitCapture={handleSubmit}
                    >
                        <Space direction="vertical" style={{ width: '100%' }}>
                            <Card title={t('myInfoModuleDetails:dividers.myInfoSettings')}>
                                <FormFields />
                            </Card>
                        </Space>
                    </Form>
                )}
            </Formik>
        </ConsolePageWithHeader>
    );
};

export default UpdateMyInfoSettingInnerPage;
